import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Award, Tag, ArrowLeft, Shield, Lock, 
  CheckCircle, Quote, Loader2, CreditCard, X, AlertTriangle 
} from 'lucide-react';
import { counselorRegistrationApi, handleRazorpayPayment } from '../../services/counselingApi'; 
import logo from '../../img/logo3.png'; 

const CounselorCheckoutPage = ({ formData, setStep, setRegistrationStatus }) => {
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState({ amount: 0, appliedCode: null, message: null });
  const [totalAmount, setTotalAmount] = useState(null);
  
  // Animation State
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [fadeQuote, setFadeQuote] = useState(true);

  const quotes = [
    { text: "Investment in knowledge pays the best interest. Welcome to the premier counseling network.", author: "Benjamin Franklin" },
    { text: "You are one step away from connecting with thousands of students needing your guidance.", author: "Vidyazon Team" },
    { text: "Secure, transparent, and built for growth. Your partnership means the world to us.", author: "Partner Relations" },
    { text: "Join a community of experts dedicated to shaping the future of education.", author: "Community Lead" },
    { text: "Your expertise is valuable. We handle the platform so you can focus on counseling.", author: "Tech Support" }
  ];

  useEffect(() => {
    loadPricing();
    const interval = setInterval(() => {
      setFadeQuote(false);
      setTimeout(() => {
        setQuoteIndex((prev) => (prev + 1) % quotes.length);
        setFadeQuote(true);
      }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadPricing = async () => {
    setLoading(true);
    try {
      const response = await counselorRegistrationApi.getPricing();
      const priceData = response.pricing;
      if (!priceData) throw new Error('Pricing data not found');
      
      setPricing(priceData);
      const total = (priceData.base_fee || 0) + (priceData.processing_fee || 0) + (priceData.platform_fee || 0);
      setTotalAmount(total);
    } catch (error) {
      console.error('❌ Failed to load pricing:', error);
      alert('Failed to load pricing. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return alert('Please enter a coupon code');
    if (!pricing) return alert('Pricing data not loaded.');

    setLoading(true);
    try {
      const orderAmount = (pricing.base_fee || 0) + (pricing.processing_fee || 0) + (pricing.platform_fee || 0);
      const response = await counselorRegistrationApi.validateCoupon(couponCode.trim(), orderAmount);

      setDiscount({
        amount: response.discountAmount || 0,
        appliedCode: response.couponCode,
        message: response.message || 'Coupon applied!',
      });
      setTotalAmount(response.finalAmount);
      alert('Coupon applied successfully!');

    } catch (error) {
      setDiscount({ amount: 0, appliedCode: null, message: null });
      const resetTotal = (pricing.base_fee || 0) + (pricing.processing_fee || 0) + (pricing.platform_fee || 0);
      setTotalAmount(resetTotal);
      alert(error.response?.data?.message || 'Invalid coupon.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Remove Coupon Function
  const handleRemoveCoupon = () => {
    setDiscount({ amount: 0, appliedCode: null, message: null });
    const resetTotal = (pricing.base_fee || 0) + (pricing.processing_fee || 0) + (pricing.platform_fee || 0);
    setTotalAmount(resetTotal);
    setCouponCode('');
  };

  const handlePayment = async () => {
    if (totalAmount === null || totalAmount <= 0) return alert('Invalid amount');
    
    setLoading(true);
    try {
      const orderResponse = await counselorRegistrationApi.createPaymentOrder({
    amount: totalAmount,
    couponApplied: discount.appliedCode || "",
});
      
      handleRazorpayPayment(
        orderResponse, 
        async (paymentData) => {
          try {
            await counselorRegistrationApi.verifyPayment(paymentData);
            if (setRegistrationStatus) setRegistrationStatus('pending_approval');
            setStep(3); 
          } catch (error) {
            alert('Verification failed. ID: ' + paymentData.payment_id);
          } finally {
            setLoading(false);
          }
        },
        (errMsg) => {
          alert(errMsg || "Payment failed.");
          setLoading(false);
        }
      );
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to initiate payment.');
      setLoading(false);
    }
  };

  if (loading && !pricing) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
        <p className="text-gray-600 font-medium">Loading secure checkout...</p>
      </div>
    );
  }

  const basePrice = pricing?.base_fee || 0;
  const processingFee = pricing?.processing_fee || 0;
  const platformFee = pricing?.platform_fee || 0;
  const subtotal = basePrice + processingFee + platformFee;

  return (
    // ✅ Adjusted Padding: pt-20 for Mobile, pt-24 for Desktop to fit your header
    <div className="min-h-screen bg-gray-50 pt-20 lg:pt-28 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
          
          {/* LEFT COLUMN: Branding & Trust (Hidden on small mobile screens to save space, visible on large) */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit space-y-6 hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <div className="flex justify-center mb-4">
                    <img src={logo} alt="Vidyazon" className="h-16 w-auto object-contain drop-shadow-sm" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Secure Payment</h2>
                <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 py-1 px-3 rounded-full w-fit mx-auto">
                    <Lock className="w-3 h-3" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">SSL Encrypted</span>
                </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 text-white relative overflow-hidden">
                <Quote className="w-8 h-8 text-white/30 mb-4" />
                <div className={`transition-opacity duration-500 ease-in-out min-h-[120px] flex flex-col justify-center ${fadeQuote ? 'opacity-100' : 'opacity-0'}`}>
                    <p className="text-lg font-medium leading-relaxed italic mb-4">"{quotes[quoteIndex].text}"</p>
                    <div className="flex items-center gap-2 mt-auto">
                        <div className="h-0.5 w-8 bg-white/50"></div>
                        <p className="text-xs font-bold tracking-wider uppercase opacity-90">{quotes[quoteIndex].author}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                <h4 className="font-bold text-gray-800 mb-4 flex items-center text-sm"><Shield className="w-4 h-4 text-indigo-600 mr-2" /> Payment Security</h4>
                <ul className="space-y-3">
                    <li className="flex items-center text-xs text-gray-600"><CheckCircle className="w-3 h-3 text-green-500 mr-2" /> Razorpay Secure Gateway</li>
                    <li className="flex items-center text-xs text-gray-600"><CheckCircle className="w-3 h-3 text-green-500 mr-2" /> Instant Activation</li>
                </ul>
            </div>
          </div>

          {/* RIGHT COLUMN: Checkout Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold flex items-center"><CreditCard className="w-5 h-5 mr-2" /> Checkout</h2>
                        <p className="text-indigo-100 text-xs opacity-90">Complete your registration</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] uppercase opacity-75">Total</p>
                        <p className="text-xl font-bold">₹{totalAmount !== null ? totalAmount.toFixed(2) : subtotal.toFixed(2)}</p>
                    </div>
                </div>

                <div className="p-6 md:p-8 space-y-6">
                    {/* Compact User Info */}
                    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 flex flex-col md:flex-row justify-between gap-4 text-sm">
                        <div>
                            <span className="text-gray-500 block text-[10px] uppercase">Counselor Name</span>
                            <span className="font-semibold text-gray-900">{formData.contact_person}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block text-[10px] uppercase">Email</span>
                            <span className="font-semibold text-gray-900">{formData.email}</span>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center"><DollarSign className="w-4 h-4 text-gray-400 mr-2" /> Payment Details</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between py-1 border-b border-gray-50"><span className="text-gray-600">Registration Fee</span><span className="font-medium text-gray-900">₹{basePrice.toFixed(2)}</span></div>
                            <div className="flex justify-between py-1 border-b border-gray-50"><span className="text-gray-600">Platform Charges</span><span className="font-medium text-gray-900">₹{platformFee.toFixed(2)}</span></div>
                            <div className="flex justify-between py-1 border-b border-gray-50"><span className="text-gray-600">Processing Fee</span><span className="font-medium text-gray-900">₹{processingFee.toFixed(2)}</span></div>
                            
                            <div className="flex justify-between pt-2 text-base"><span className="font-bold text-gray-700">Subtotal</span><span className="font-bold text-gray-900">₹{subtotal.toFixed(2)}</span></div>

                            {/* ✅ REMOVE COUPON OPTION */}
                            {discount.amount > 0 && (
                                <div className="flex justify-between items-center bg-green-50 p-2 rounded-lg border border-green-100 text-green-700 animate-in fade-in slide-in-from-top-1 mt-2">
                                    <div className="flex items-center text-xs font-medium">
                                        <Tag className="w-3 h-3 mr-2" /> 
                                        <span>Coupon ({discount.appliedCode})</span>
                                        <button 
                                            onClick={handleRemoveCoupon}
                                            className="ml-2 p-1 hover:bg-green-200 rounded-full transition-colors text-green-800"
                                            title="Remove Coupon"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <span className="font-bold text-sm">- ₹{discount.amount.toFixed(2)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Coupon Input */}
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            placeholder="COUPON CODE" 
                            className="flex-1 p-2.5 border border-gray-300 rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 uppercase text-sm"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            disabled={discount.amount > 0} 
                        />
                        <button 
                            onClick={handleApplyCoupon}
                            disabled={loading || !couponCode || discount.amount > 0}
                            className={`px-4 py-2.5 rounded-lg font-bold text-sm transition ${discount.amount > 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                        >
                            APPLY
                        </button>
                    </div>

                    {/* Total & Pay */}
                    <div className="pt-6 border-t border-gray-200">
                        <div className="flex justify-between items-end mb-6">
                            <span className="text-xs font-bold text-gray-400 uppercase">Total Payable</span>
                            <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                                ₹{totalAmount !== null ? totalAmount.toFixed(2) : subtotal.toFixed(2)}
                            </span>
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row gap-3">
                            <button onClick={() => setStep(1)} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-bold hover:bg-gray-50 transition flex items-center justify-center gap-2">
                                <ArrowLeft className="w-4 h-4" /> Edit
                            </button>
                            <button onClick={handlePayment} disabled={loading || totalAmount <= 0} className="flex-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-3 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2">
                                {loading ? <Loader2 className="w-5 h-5 animate-spin"/> : <CreditCard className="w-5 h-5"/>}
                                {loading ? 'Processing...' : 'Pay Now'}
                            </button>
                        </div>
                        <div className="mt-4 flex items-center justify-center gap-1 text-[10px] text-gray-400 uppercase tracking-wide">
                            <Lock className="w-3 h-3" /> Secure SSL Payment
                        </div>
                    </div>

                </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CounselorCheckoutPage;